import { React, useEffect, useState, useRef } from 'react'
import { Box, Typography, Icon } from '@mui/material'
import Bugsnag from '@bugsnag/js'
import AdxInput from 'components/custom/AdxInput'
import AdxDropdownWithFilter from 'components/reusable/AdxDropdownWithFilter'
import AdxMenuItem from 'components/custom/AdxMenuItem'
import AdxDropdown from 'components/custom/AdxDropdown'
import AdxErrorMessage from 'components/custom/AdxErrorMessage'
import Avatar from '@mui/material/Avatar'
import FaceIcon from '@mui/icons-material/Face'
import { Formik, Form, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import { useMutation, useQueryClient } from 'react-query'
import { handleValidationErrors, handleScroll } from 'services/helper/handleValidationErrors'

// API
import { postEmploymentData } from 'services/api/modules/staff-management/staffManagementDashboard'
import AdxButton from 'components/custom/AdxButton'

const JobInformation = ({ data, employeeID, departmentList, windowCheckEmployeeFile }) => {
  const queryClient = useQueryClient()
  const formikRef = useRef()

  // form validation
  const validationSchema = Yup.object({
    internal_employee_id: Yup.string()
      .nullable()
      .required('Employee ID is required')
      .matches(/^[a-zA-Z0-9]+$/, 'Employee ID must be alphanumeric'),
    designation: Yup.string()
      .nullable()
      .required('Job Designation is required')
      .matches(/^[A-Z\d\-()\s]+$/i, 'Job Designation must be valid'),
    job_description: Yup.string().nullable().required('Job Description is required'),
    department_id: Yup.number().nullable().required('Department is required'),
    reporting_to_id: !data?.data?.head_organisation
      ? Yup.object().nullable().required('Reporting Manager is required')
      : Yup.object().nullable(),
  })

  const validationProfileDetail = Yup.object({
    job_description: Yup.string().nullable().required('Job Description is required'),
  })

  const [disable, setDisable] = useState(true)
  const [initialValues, setInitialValues] = useState({
    internal_employee_id: '',
    designation: '',
    job_description: '',
    department_id: '',
    reporting_to_id: {
      id: '',
      name: '',
      designation: '',
      profile_image_url: '',
    },
  })
  // State and function for Dropdown With Filter Functions
  const [dropdownFilterDepartmentManagerOptions, setDropdownFilterDepartmentManagerOptions] =
    useState([])
  const cancelEditForm = (resetForm) => {
    resetForm()
    setDisable(true)
  }

  const mutation = useMutation((value) => postEmploymentData(employeeID, value), {
    onSuccess: (response) => {
      queryClient.invalidateQueries(['detailedEmployeeData', employeeID])
      queryClient.invalidateQueries(['employeeEmploymentData', employeeID])
      window.scrollTo(0, 0)
      setDisable(true)
    },
    onError: (error) => {
      if (error.status === 422) {
        handleValidationErrors(error, formikRef)
        if (error.data.errors) {
          handleScroll(error.data.errors)
        }
      } else {
        Bugsnag.notify(error.data)
      }
    },
  })

  const handleSubmitJobInformation = (values, actions) => {
    if (windowCheckEmployeeFile) {
      let newValues = {
        ...values,
        reporting_to_id: values.reporting_to_id.id,
      }
      mutation.mutate(newValues)
    } else {
      mutation.mutate({ job_description: values.job_description })
    }
  }

  useEffect(() => {
    if (data) {
      setInitialValues({
        internal_employee_id: data?.data?.internal_employee_id
          ? data?.data?.internal_employee_id
          : '',
        designation: data?.data?.designation,
        job_description: data?.data?.job_description ? data?.data?.job_description : '',
        department_id: data?.data?.department_id,
        reporting_to_id: data?.data?.reporting_to_id
          ? {
              id: data?.data?.reporting_to_id,
              name: data?.data?.reporting_to_name,
              designation: data?.data?.reporting_to_designation,
              profile_image_url: data?.data?.reporting_to_image,
            }
          : null,
      })
      setDropdownFilterDepartmentManagerOptions(data?.data?.all_staff)
    }
  }, [data])

  const handleCheckRef = async (handleFormikSubmit) => {
    await handleFormikSubmit()
    const { errors = {} } = formikRef.current
    handleScroll(errors)
  }

  return (
    <Formik
      onSubmit={handleSubmitJobInformation}
      initialValues={initialValues}
      enableReinitialize
      validationSchema={windowCheckEmployeeFile ? validationSchema : validationProfileDetail}
      innerRef={formikRef}
    >
      {({
        values,
        touched,
        errors,
        handleChange,
        handleSubmit,
        isValid,
        dirty,
        setFieldValue,
        resetForm,
      }) => (
        <Form>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              borderBottom: '1px solid',
              alignItems: 'center',
              borderColor: 'gray-line.main',
              p: 2,
            }}
          >
            <Typography color="light-gray-text.main" fontWeight={500}>
              Job information
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              borderBottom: '1px solid',
              borderColor: 'gray-line.main',
              p: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: 2,
              }}
            >
              <Box sx={{ width: '40%' }}>
                <AdxInput
                  label="Employee ID"
                  disabled={windowCheckEmployeeFile ? disable : true}
                  sxLabel={{
                    color: disable
                      ? 'light-gray-text.main'
                      : 'dark-text.main' /*, fontSize: "30px!important" */,
                  }}
                  sxInput={{
                    color: 'light-gray-text.main',
                    backgroundColor: 'white.main',
                  }}
                  size="standard"
                  placeholder="Employee ID"
                  value={values.internal_employee_id}
                  name="internal_employee_id"
                  onChange={handleChange}
                  errorMessage={
                    <ErrorMessage name="internal_employee_id" component={AdxErrorMessage} />
                  }
                  isErrorShown={touched.internal_employee_id && errors.internal_employee_id}
                />
              </Box>
              <Box sx={{ width: '60%' }}>
                <AdxInput
                  label="Work Email"
                  sxLabel={{
                    color: disable
                      ? 'light-gray-text.main'
                      : 'dark-text.main' /*, fontSize: "30px!important" */,
                  }}
                  sxInput={{
                    color: 'light-gray-text.main',
                    backgroundColor: 'white.main',
                  }}
                  size="standard"
                  placeholder="Work Email"
                  value={data && data?.data?.work_email}
                  disabled
                />
              </Box>
            </Box>
            <AdxInput
              label="Job designation"
              disabled={windowCheckEmployeeFile ? disable : true}
              sxLabel={{
                color: disable
                  ? 'light-gray-text.main'
                  : 'dark-text.main' /*, fontSize: "30px!important" */,
              }}
              sxInput={{
                color: 'light-gray-text.main',
                backgroundColor: 'white.main',
              }}
              size="standard"
              placeholder="E.g: Mechanical Engineer - Junior (Gas Turbine)"
              value={values.designation}
              name="designation"
              onChange={handleChange}
              sx={{ width: '100%' }}
              errorMessage={<ErrorMessage name="designation" component={AdxErrorMessage} />}
              isErrorShown={touched.designation && errors.designation}
            />
            <AdxInput
              label="Job Description"
              disabled={disable}
              sxLabel={{
                color: disable
                  ? 'light-gray-text.main'
                  : 'dark-text.main' /*, fontSize: "30px!important" */,
              }}
              sxInput={{
                color: 'light-gray-text.main',
                backgroundColor: 'white.main',
              }}
              size="standard"
              placeholder="E.g: Research and develop on the performance of gas turbine."
              value={values.job_description}
              name="job_description"
              onChange={handleChange}
              sx={{ width: '100%' }}
              multiline
              minRows={3}
              errorMessage={<ErrorMessage name="job_description" component={AdxErrorMessage} />}
              isErrorShown={touched.job_description && errors.job_description}
            />
            {/* Department */}
            <AdxDropdown
              placeholder="Department"
              label="Department"
              disabled={windowCheckEmployeeFile ? disable : true}
              sx={{ width: { xs: '100%', lg: '100%' } }}
              sxLabel={{
                color: disable
                  ? 'light-gray-text.main'
                  : 'dark-text.main' /*, fontSize: "30px!important" */,
              }}
              sxDropdown={{
                color: 'light-gray-text.main',
                backgroundColor: 'white.main',
              }}
              name="department_id"
              value={values.department_id}
              onChange={handleChange}
              errorMessage={<ErrorMessage name="department_id" component={AdxErrorMessage} />}
              isErrorShown={touched.department_id && errors.department_id}
            >
              {departmentList &&
                departmentList?.map((item) => (
                  <AdxMenuItem value={item.id} selectedvalue={values.department_id}>
                    {item.name}
                  </AdxMenuItem>
                ))}
            </AdxDropdown>
            {/* Reporting Manager */}
            <AdxDropdownWithFilter
              label="Reporting Manager"
              sxLabel={{
                color: disable
                  ? 'light-gray-text.main'
                  : 'dark-text.main' /*, fontSize: "30px!important" */,
              }}
              disabled={windowCheckEmployeeFile ? disable : true}
              allOptions={data ? data?.data?.all_staff : []}
              setOptions={setDropdownFilterDepartmentManagerOptions}
              filters={['name', 'designation', 'department_name']}
              sx={{
                width: '100%',
                backgroundColor: 'white.main',
              }}
              value={values.reporting_to_id}
              name="reporting_to_id"
              onChange={handleChange}
              renderValue={(option) => {
                return (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {option.profile_image_url ? (
                      <Avatar
                        src={option.profile_image_url}
                        sx={{
                          backgroundColor: 'gray-background.main',
                          width: '30px',
                          height: '30px',
                          mx: 1,
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          backgroundColor: 'gray-background.main',
                          width: '30px',
                          height: '30px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '50%',
                          mx: 1,
                        }}
                      >
                        <FaceIcon fontSize="20px" color="alternate" />
                      </Box>
                    )}
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                      }}
                    >
                      <Typography variant="body1">{option.name}</Typography>
                      <Typography variant="caption">{option.designation}</Typography>
                    </Box>
                  </Box>
                )
              }}
            >
              {dropdownFilterDepartmentManagerOptions.map((item) => (
                <AdxMenuItem value={item} selectedvalue={values.reporting_to_id}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {item.profile_image_url ? (
                      <Avatar
                        src={item.profile_image_url}
                        sx={{
                          backgroundColor: 'gray-background.main',
                          width: '40px',
                          height: '40px',
                          mx: 1,
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          backgroundColor: 'gray-background.main',
                          width: '40px',
                          height: '40px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '50%',
                          mx: 1,
                        }}
                      >
                        <FaceIcon fontSize="20px" color="alternate" />
                      </Box>
                    )}
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                      }}
                    >
                      <Typography variant="body1">{item.name}</Typography>
                      <Typography variant="caption">{item.designation}</Typography>
                    </Box>
                  </Box>
                </AdxMenuItem>
              ))}
            </AdxDropdownWithFilter>
            {touched.reporting_to_id && errors.reporting_to_id && (
              <ErrorMessage name="reporting_to_id" component={AdxErrorMessage} />
            )}
            {!disable && (
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  columnGap: 1,
                }}
              >
                <AdxButton
                  variant={'outlined'}
                  onClick={() => cancelEditForm(resetForm)}
                  sx={{ width: 0.2 }}
                >
                  Cancel
                </AdxButton>
                <AdxButton
                  variant={'contained'}
                  onClick={() => handleCheckRef(handleSubmit)}
                  sx={{ width: 0.2 }}
                >
                  Save
                </AdxButton>
              </Box>
            )}
          </Box>
          {disable && (
            <Box sx={{ p: 0.5 }}>
              <AdxButton
                variant={'ghost'}
                sx={{
                  pl: 2,
                  display: 'flex',
                  justifyContent: 'center',
                  width: '100%',
                }}
                onClick={() => setDisable(false)}
              >
                <Icon sx={{ fontSize: '15px', color: 'alternate.main' }}>edit</Icon>
                <Typography fontWeight={500} color="alternate.main" ml={1.25}>
                  Edit job Information details
                </Typography>
              </AdxButton>
            </Box>
          )}
        </Form>
      )}
    </Formik>
  )
}

export default JobInformation
